import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Tab,
  Nav,
  Button,
  ListGroup,
  Carousel,
  Accordion,
  Form,
  InputGroup,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";

import NavBar from "./NavBar";
import CoverPage from "./CoverPage";
import ContactPage from "./ContactPage";
import FooterPage from "./FooterPage";
import FloatingContact from "../components/FloatingContact";
import { toggleWishlist, fetchWishlist } from "../services/wishlistService";
import { bookTrip } from "../services/bookingService";

const DynamicPage = ({ trip, categories }) => {
  const [user, setUser] = useState(null);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [numTravellers, setNumTravellers] = useState(0);
  const [travellers, setTravellers] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      fetchWishlist(parsed.id).then((data) => {
        setWishlist(data);
        setIsWishlisted(data.some((t) => t.path === trip.path));
      });
    }
    handleTravellerCountChange(0);
  }, [trip.path]);

  const handleTravellerCountChange = (value) => {
    const count = Number(value);
    setNumTravellers(count);
    setTravellers(
      Array.from(
        { length: count },
        (_, i) => travellers[i] || { name: "", age: "", phone: "" }
      )
    );
  };

  const isValidTravellers = travellers.every(
    (t) =>
      t.name.trim() &&
      t.age &&
      t.phone.trim().length === 10 &&
      /^\d{10}$/.test(t.phone.trim())
  );

  const handleWishlistClick = async () => {
    if (!user) {
      toast.warning("Please login to use wishlist");
      return;
    }
    setLoading(true);
    try {
      const result = await toggleWishlist(user.id, trip.path);
      if (result.includes("Added")) {
        setWishlist((prev) => [...prev, trip]);
        setIsWishlisted(true);
      } else {
        setWishlist((prev) => prev.filter((t) => t.path !== trip.path));
        setIsWishlisted(false);
      }
      toast.success(result);
    } catch (err) {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <NavBar categories={categories} />
      <CoverPage imageSrc={trip.coverUrl} altText={trip.title} />
      <FloatingContact />
      <Container className="my-5">
        <Row>
          <Col md={4}>
            <Card className="mb-4 shadow-sm">
              <Card.Body>
                <h4>
                  <del className="text-muted">₹{trip.originalPrice}</del>{" "}
                  <span className="text-success fw-bold">₹{trip.price}</span>
                </h4>
                <p className="text-muted mb-3">{trip.availability}</p>
                <Button
                  variant={isWishlisted ? "danger" : "outline-danger"}
                  className="w-100 fw-semibold"
                  onClick={handleWishlistClick}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Please wait...
                    </>
                  ) : isWishlisted ? (
                    "❤️ In Wishlist"
                  ) : (
                    "🤍 Add to Wishlist"
                  )}
                </Button>
                <hr className="my-4" />
                <Form.Group className="mb-3" controlId="numTravellers">
                  <Form.Label className="fw-semibold">
                    Number of Travellers
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={numTravellers}
                    onChange={(e) => handleTravellerCountChange(e.target.value)}
                    placeholder="Enter number of travellers"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="selectDate">
                  <Form.Label className="fw-semibold">
                    Select Travel Date
                  </Form.Label>
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    minDate={new Date()}
                    placeholderText="DD/MM/YYYY"
                    className="form-control"
                    disabled={
                      !numTravellers ||
                      isNaN(numTravellers) ||
                      numTravellers < 1
                    }
                  />
                </Form.Group>

                {numTravellers > 0 &&
                  travellers.map((traveller, index) => (
                    <div key={index} className="border rounded p-3 mb-3">
                      <h6 className="fw-bold">Traveller {index + 1}</h6>
                      <Form.Group className="mb-2">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={traveller.name}
                          onChange={(e) => {
                            const updated = [...travellers];
                            updated[index].name = e.target.value;
                            setTravellers(updated);
                          }}
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Age</Form.Label>
                        <Form.Control
                          type="number"
                          value={traveller.age}
                          onChange={(e) => {
                            const updated = [...travellers];
                            updated[index].age = e.target.value;
                            setTravellers(updated);
                          }}
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Phone</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>+91</InputGroup.Text>
                          <Form.Control
                            type="tel"
                            value={traveller.phone}
                            onChange={(e) => {
                              const updated = [...travellers];
                              updated[index].phone = e.target.value;
                              setTravellers(updated);
                            }}
                            required
                            isInvalid={
                              traveller.phone.trim().length !== 10 ||
                              !/^\d{10}$/.test(traveller.phone.trim())
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            Phone number must be 10 digits.
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    </div>
                  ))}

                <Button
                  variant="warning"
                  className="w-100 fw-bold"
                  disabled={!selectedDate || !isValidTravellers}
                  onClick={async () => {
                    if (!user) {
                      toast.warning("Please login to book the trip");
                      return;
                    }
                    try {
                      const formattedTravellers = travellers.map((t) => ({
                        ...t,
                        phone: "+91" + t.phone.trim(),
                      }));
                      await bookTrip({
                        userId: user.id,
                        tripId: trip.id,
                        tripTitle: trip.title,
                        noOfTravellers: Number(numTravellers),
                        travelDate: selectedDate.toISOString().split("T")[0],
                        travellers: formattedTravellers,
                      });
                      toast.success(
                        `Booking confirmed for ${
                          trip.title
                        } on ${selectedDate.toLocaleDateString("en-IN")}`
                      );
                    } catch (err) {
                      if (
                        err.response &&
                        err.response.status === 400 &&
                        err.response.data.includes("already booked")
                      ) {
                        toast.warning(err.response.data);
                      } else {
                        toast.error("Booking failed. Try again.");
                        console.error(
                          "Booking failed:",
                          err.response?.data || err.message
                        );
                      }
                    }
                  }}
                >
                  🚀 Book Now
                </Button>
              </Card.Body>
            </Card>
          </Col>
          {/* Tabs Section */}
          <div className="my-5">
            <Tab.Container defaultActiveKey="overview">
              <Row className="mb-4">
                <Nav variant="tabs" className="justify-content-center">
                  <Nav.Item>
                    <Nav.Link eventKey="overview">Overview</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="itinerary">Itinerary</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="gallery">Gallery</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="faqs">FAQs</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Row>

              <Tab.Content>
                {/* Overview Tab */}
                <Tab.Pane eventKey="overview">
                  <h2 className="mb-4">Overview</h2>
                  <p>{trip.overviewText}</p>

                  <h5 className="mt-4">Departure & Return Location</h5>
                  <p>{trip.departureLocation}</p>

                  <h5>Departure Time</h5>
                  <p>{trip.departureTime}</p>

                  <h5>Price Includes</h5>
                  {trip.priceIncludes.map((item, index) => (
                    <p key={index}>
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      {item}
                    </p>
                  ))}

                  <h5>Price Excludes</h5>
                  {trip.priceExcludes.map((item, index) => (
                    <p key={index}>
                      <i className="bi bi-x-circle-fill text-danger me-2"></i>
                      {item}
                    </p>
                  ))}
                </Tab.Pane>

                {/* Itinerary Tab */}
                <Tab.Pane eventKey="itinerary">
                  {trip.itinerary.map((dayPlan, index) => (
                    <div key={index} className="mb-4">
                      <h5 className="bg-light p-3 rounded fw-bold">
                        {dayPlan.day}{" "}
                        <span className="text-primary">{dayPlan.title}</span>
                      </h5>
                      {dayPlan.description && <p>{dayPlan.description}</p>}
                      {dayPlan.points && (
                        <ListGroup variant="flush">
                          {dayPlan.points.map((point, idx) => (
                            <ListGroup.Item key={idx}>{point}</ListGroup.Item>
                          ))}
                        </ListGroup>
                      )}
                      {dayPlan.stayInfo && (
                        <div className="bg-warning-subtle mt-4 p-3 rounded">
                          <strong>STAY TYPE –</strong> {dayPlan.stayInfo}
                        </div>
                      )}
                    </div>
                  ))}
                </Tab.Pane>

                {/* Gallery Tab */}
                <Tab.Pane eventKey="gallery">
                  <h3 className="mb-4">
                    <i className="bi bi-image-fill me-2"></i> Gallery
                  </h3>
                  <Carousel>
                    {trip.galleryImages.map((imgSrc, idx) => (
                      <Carousel.Item key={idx}>
                        <img
                          className="d-block w-100 rounded"
                          src={imgSrc}
                          alt={`Slide ${idx + 1}`}
                          style={{ maxHeight: "500px", objectFit: "cover" }}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </Tab.Pane>

                {/* FAQs Tab */}
                <Tab.Pane eventKey="faqs">
                  <h3 className="mb-4">
                    <i className="bi bi-question-circle-fill me-2"></i> FAQs
                  </h3>
                  <Accordion defaultActiveKey="0">
                    {trip.faqs.map((faq, idx) => (
                      <Accordion.Item eventKey={idx.toString()} key={idx}>
                        <Accordion.Header>{faq.question}</Accordion.Header>
                        <Accordion.Body>{faq.answer}</Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>

          {/* Why Book With Us */}
          <Col md={4}>
            <Card>
              <Card.Header>
                <strong>Why Book With Us?</strong>
              </Card.Header>
              <ListGroup variant="flush">
                <ListGroup.Item>Safety First</ListGroup.Item>
                <ListGroup.Item>Experienced Team</ListGroup.Item>
                <ListGroup.Item>
                  Sustainability and Responsible Travel
                </ListGroup.Item>
                <ListGroup.Item>Creating Unforgettable Memories</ListGroup.Item>
              </ListGroup>
            </Card>
          </Col>
        </Row>
      </Container>
      <ContactPage />
      <FooterPage />
    </div>
  );
};

export default DynamicPage;
